import { ZuiBaseElement } from '@zywave/zui-base';
import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { style } from './zui-pager-css.js';
import { repeat } from 'lit/directives/repeat.js';
import { classMap } from 'lit/directives/class-map.js';
import { debounce } from '@zywave/zui-base/dist/utils/debounce.js';
import { screenBreakpoints } from '@zywave/zui-base/dist/utils/breakpoints.js';
import '@zywave/zui-icons';
import type { PropertyValues } from 'lit';

class PagerButton {
  constructor(text: string, disabled: boolean) {
    this.text = text;
    this.disabled = disabled;
  }

  text: string;
  disabled: boolean;
}

/**
 * Used to standardize pagination.
 *
 * @element zui-pager
 *
 * @event change - When there's a change on `<zui-pager>` raise this event with `currentPage` included as details
 *
 * @cssprop [--zui-pager-color=#000] - Used in several shadow DOM elements to theme `<zui-pager>` to a color
 */
export class ZuiPager extends ZuiBaseElement {
  /**
   * The total number of pages to display in the pager. Should NOT be specified if using `pageSize` / `page-size` and `totalCount` / `total-count`
   */
  @property({ type: Number, attribute: 'total-pages' })
  totalPages = 1;

  /**
   * The currently selected page
   */
  @property({ type: Number, reflect: true, attribute: 'current-page' })
  currentPage = 1;

  /**
   * The type of pager
   * @type { 'standard' | 'lite' | 'select' }
   */
  @property({ type: String })
  type: ZuiPagerType = 'standard';

  /**
   * (optional): to configure where a user will be navigated when changing pages (e.g. deeplinking to pages)
   */
  @property({ type: String, attribute: 'href-format' })
  hrefFormat: undefined | string = undefined;

  /**
   * Provide an input where users can specify a page number to go to
   */
  @property({ type: Boolean, attribute: 'hide-input' })
  hideInput = false;

  /**
   * The size of a page of results. MUST be used with `totalCount` / `total-count`. DO NOT use this with `totalPages` / `total-pages`.
   */
  @property({ type: Number, attribute: 'page-size' })
  pageSize: undefined | number = undefined;

  /**
   * The total number of results. MUST be used with `pageSize` / `page-size`. DO NOT use this with `totalPages` / `total-pages`.
   */
  @property({ type: Number, attribute: 'total-count' })
  totalCount: undefined | number = undefined;

  #shouldCalculateTotalPages: undefined | boolean = undefined;
  #isMobile: boolean | undefined = undefined;

  private get _totalPages() {
    return this.#shouldCalculateTotalPages
      ? this._calculateTotalPages(this.pageSize, this.totalCount)
      : this.totalPages;
  }

  constructor() {
    super();

    this.#shouldCalculateTotalPages = false;
    this._setupMediaQueryListener();
  }

  connectedCallback() {
    super.connectedCallback();
    const isNullOrUndefined = (o: number): boolean => {
      return o === undefined || o === null;
    };

    if (!(isNullOrUndefined(this.pageSize) || isNullOrUndefined(this.totalCount))) {
      this.#shouldCalculateTotalPages = true;
    }
  }

  updated(changedProps: PropertyValues) {
    super.updated(changedProps);

    if (changedProps.get('currentPage')) {
      this.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: { value: this.currentPage } }));
    }
  }

  static get styles() {
    return [super.styles, style];
  }

  render() {
    if (!this.currentPage) {
      return html``;
    }

    const backButton = html`
      ${this.hrefFormat
        ? html`
            <a
              class="pager-button back ${classMap({
                disabled: this.currentPage <= 1,
              })}"
              href="${this.currentPage <= 1 ? '' : this._formatHref((+this.currentPage - 1).toString())}"
              @click="${this._handleDisabledAnchorClick}"
              ><zui-icon icon="zui-chevron-left"></zui-icon
            ></a>
          `
        : html`
            <button class="pager-button back" ?disabled="${this.currentPage <= 1}" @click="${this._pageBackwards}"
              ><zui-icon icon="zui-chevron-left"></zui-icon
            ></button>
          `}
    `;

    const forwardButton = html`
      ${this.hrefFormat
        ? html`
            <a
              class="pager-button forward ${classMap({
                disabled: this.currentPage >= this._totalPages,
              })}"
              href="${this.currentPage >= this._totalPages ? '' : this._formatHref((+this.currentPage + 1).toString())}"
              @click="${this._handleDisabledAnchorClick}"
              ><zui-icon icon="zui-chevron-right"></zui-icon
            ></a>
          `
        : html`
            <button
              class="pager-button forward"
              ?disabled="${this.currentPage >= this._totalPages}"
              @click="${this._pageForwards}"
              ><zui-icon icon="zui-chevron-right"></zui-icon
            ></button>
          `}
    `;

    let pagerTemplate;
    switch (this.type) {
      case 'standard':
        pagerTemplate = this.#isMobile ? this._renderSelect() : this._renderStandard();
        break;
      case 'lite':
        pagerTemplate = this._renderLite();
        break;
      case 'select':
        pagerTemplate = this._renderSelect();
        break;
      default:
        pagerTemplate = this.#isMobile ? this._renderSelect() : this._renderStandard();
        break;
    }
    return html` <div class="pages">${backButton}${pagerTemplate}${forwardButton}</div>${this._renderPageInput()} `;
  }

  private _renderPageInput() {
    return this.hideInput
      ? html``
      : html`
          <input
            class="input"
            id="input"
            .value="${this.currentPage}"
            @change="${(e: Event) => this._goToPage(+(e.target as HTMLInputElement).value)}"
          />
        `;
  }

  private _renderStandard() {
    const items = this._generatePages();
    const anchorItems = html`
      ${repeat(
        items,
        (i: PagerButton) => i.text,
        (i: PagerButton) =>
          html`
            ${this.hrefFormat
              ? html`
                  <a
                    class="pager-button page ${classMap({
                      disabled: (+this.currentPage).toString() === i.text || i.disabled,
                      current: (+this.currentPage).toString() === i.text,
                    })}"
                    href="${(+this.currentPage).toString() === i.text || i.disabled ? '' : this._formatHref(i.text)}"
                    @click="${(e: Event): boolean | void => this._handleDisabledAnchorClick(e)}"
                    >${i.text}</a
                  >
                `
              : html`<button class="pager-button page ${classMap({
                  current: (+this.currentPage).toString() === i.text,
                })}" ?disabled="${this.currentPage.toString() === i.text || i.disabled}" @click="${() =>
                  this._goToPage(+i.text)}">${i.text}</a>`}
          `
      )}
    `;

    return html` ${anchorItems} `;
  }

  private _renderLite() {
    return html``;
  }

  private _renderSelect() {
    const options: PagerButton[] = Array.from(
      new Array(this._totalPages),
      (_, i) => new PagerButton(`${i + 1}`, false)
    );
    const optionsHTML = html`
      ${repeat(
        options,
        (i: PagerButton) => i,
        (i: PagerButton) => html` <option ?selected="${i.text === (+this.currentPage).toString()}">${i.text}</option> `
      )}
    `;
    return html`
      <div class="select-container">
        <div class="select-mock">
          <span class="current">${this.currentPage}</span> <span class="of">of</span> <span>${this._totalPages}</span>
          <zui-icon icon="zui-caret-down"></zui-icon>
        </div>
        <select
          class="select-overlay"
          @change="${(e: Event) => this._goToPage(+(e.target as HTMLSelectElement).value)}"
        >
          ${optionsHTML}
        </select>
      </div>
    `;
  }

  private _formatHref(i: string) {
    return this.hrefFormat ? this.hrefFormat.replace('{page}', i) : '';
  }

  private _goToPage(page: number) {
    if (page >= 1 && page <= this._totalPages && page !== +this.currentPage) {
      this.currentPage = page;
    }
  }

  private _generatePages() {
    const maxButtons = 7;
    const ellipsisBreakpoint = 4;
    const buttons: PagerButton[] = [];

    if (this._totalPages <= maxButtons) {
      for (let i = 1; i <= this._totalPages; i++) {
        buttons[i - 1] = new PagerButton(i.toString(), false);
      }
    } else {
      buttons[0] = new PagerButton('1', false);
      buttons[6] = new PagerButton(this._totalPages.toString(), false);

      if (this.currentPage < ellipsisBreakpoint) {
        buttons[1] = new PagerButton('2', false);
        buttons[2] = new PagerButton('3', false);
        buttons[3] = new PagerButton('4', false);
        buttons[4] = new PagerButton('5', false);
        buttons[5] = new PagerButton('...', true);
      } else if (this._totalPages - this.currentPage < ellipsisBreakpoint) {
        buttons[1] = new PagerButton('...', true);
        buttons[2] = new PagerButton((this._totalPages - 4).toString(), false);
        buttons[3] = new PagerButton((this._totalPages - 3).toString(), false);
        buttons[4] = new PagerButton((this._totalPages - 2).toString(), false);
        buttons[5] = new PagerButton((this._totalPages - 1).toString(), false);
      } else {
        buttons[1] = new PagerButton('...', true);
        buttons[2] = new PagerButton((this.currentPage - 1).toString(), false);
        buttons[3] = new PagerButton(this.currentPage.toString(), false);
        buttons[4] = new PagerButton((Number(this.currentPage) + 1).toString(), false);
        buttons[5] = new PagerButton('...', true);
      }
    }
    return buttons;
  }

  private _handleDisabledAnchorClick(e: Event) {
    if ((e.target as HTMLElement).classList.contains('disabled')) {
      e.preventDefault();
      return false;
    }
  }

  private _calculateTotalPages(pageSize: number, totalCount: number) {
    return Math.floor((totalCount + pageSize - 1) / pageSize);
  }

  private _pageBackwards() {
    this._goToPage(this.currentPage - 1);
  }

  private _pageForwards() {
    this._goToPage(this.currentPage + 1);
  }

  private _setupMediaQueryListener() {
    const xsmallMq = window.matchMedia(`(max-width: ${screenBreakpoints.xsmall})`);

    xsmallMq.addEventListener('change', (e: MediaQueryListEvent) => {
      debounce(() => {
        this.#isMobile = e.matches;
        this.requestUpdate();
      }, 100);
    });

    this.#isMobile = xsmallMq.matches;
  }
}

window.customElements.define('zui-pager', ZuiPager);

export type ZuiPagerType = 'standard' | 'lite' | 'select';

declare global {
  interface HTMLElementTagNameMap {
    'zui-pager': ZuiPager;
  }
}
